import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, ExternalLink, MailOpen } from "lucide-react";
import { UserDataContext } from "../context/User";

const NotificationDropdown = ({ isBlack = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const {
    notifications = [],
    unreadNotificationCount = 0,
    markAllNotificationsRead,
    markNotificationRead,
  } = useContext(UserDataContext);

  useEffect(() => {
    const handler = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const recentNotifications = notifications.slice(0, 5);

  const formatNotificationTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  const openNotificationLink = (link) => {
    if (!link) return;
    if (!link.startsWith("/") && !/^https?:\/\//i.test(link)) return;

    setIsOpen(false);
    if (/^https?:\/\//i.test(link)) {
      window.location.href = link;
      return;
    }

    const appPath = link.replace(/^\/lwbl/, "") || "/";
    navigate(appPath);
  };

  const handleNotificationClick = async (notification) => {
    await markNotificationRead(notification.id);
    openNotificationLink(notification.link);
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className={`relative flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${
          isBlack
            ? "border-gray-200 bg-white/80 text-gray-800 hover:border-cyan-300 hover:text-cyan-700"
            : "border-white/20 bg-white/10 text-white hover:bg-white/20"
        }`}
        aria-label="Notifications"
      >
        <Bell size={18} />
        {unreadNotificationCount > 0 && (
          <span className="absolute -right-1 -top-1 min-w-[18px] rounded-full bg-red-500 px-1 text-center text-[10px] font-bold leading-[18px] text-white">
            {unreadNotificationCount > 9 ? "9+" : unreadNotificationCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 z-50 mt-3 w-80 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl">
            <div className="border-b border-gray-100 bg-gradient-to-r from-[#B6FCFF]/35 to-[#00C3FF]/20 px-4 py-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-cinzel text-base font-bold text-gray-900">
                    Notifications
                  </p>
                  <p className="font-outfit text-xs text-gray-500">
                    {unreadNotificationCount} unread
                  </p>
                </div>
                {unreadNotificationCount > 0 && (
                  <button
                    type="button"
                    onClick={handleMarkAllRead}
                    className="inline-flex items-center gap-1 rounded-md bg-white/70 px-2.5 py-1.5 font-outfit text-[11px] font-semibold text-cyan-700 hover:bg-white"
                  >
                    <CheckCheck size={13} />
                    Read all
                  </button>
                )}
              </div>
            </div>

            <div className="max-h-80 overflow-y-auto">
              {recentNotifications.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-gray-100 text-gray-300">
                    <Bell size={22} />
                  </div>
                  <p className="font-outfit text-sm font-semibold text-gray-700">
                    No notifications yet
                  </p>
                  <p className="mt-1 font-outfit text-xs text-gray-400">
                    New messages will appear here.
                  </p>
                </div>
              ) : (
                recentNotifications.map((notification) => {
                  const unread = !notification.readAt;
                  return (
                    <button
                      type="button"
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`w-full px-4 py-3 text-left transition-colors hover:bg-gray-50 ${
                        unread ? "bg-cyan-50/60" : "bg-white"
                      }`}
                    >
                      <div className="flex gap-3">
                        <div
                          className={`mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ${
                            unread ? "bg-cyan-100 text-cyan-700" : "bg-gray-100 text-gray-400"
                          }`}
                        >
                          <MailOpen size={15} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="truncate font-outfit text-sm font-semibold text-gray-800">
                              {notification.title}
                            </p>
                            <span className="flex-shrink-0 font-outfit text-[10px] text-gray-400">
                              {formatNotificationTime(notification.createdAt)}
                            </span>
                          </div>
                          <p className="mt-1 line-clamp-2 font-outfit text-xs leading-5 text-gray-500">
                            {notification.message}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            <div className="border-t border-gray-100 p-3">
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  navigate("/notifications");
                }}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-gray-900 px-3 py-2.5 font-outfit text-xs font-semibold text-white hover:bg-cyan-600"
              >
                <ExternalLink size={14} />
                View all notifications
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationDropdown;
